<template>
  <div class="row">
    <div
      v-if="noStyleColors && !hideFilters && !styleColorsLoading"
      class="col-12 text-center">
      <b>No Results Found</b><br>
      <div v-if="selectedFilters.length > 0">
        <p>Try removing filters to see more options</p><br>
        <TextLink
          class="ps-2"
          @click="emit('clear-all-filters')">
          Clear All Filters
        </TextLink>
      </div>
    </div>
    <template v-for="(styleColor, index) in styleColorsWithCTA">
      <StyleColorPreview
        v-if="!styleColor.cta"
        :key="`${styleColorSet.source || styleColorSet.id}-${styleColor.id}`"
        :class="styleColorPreviewClass"
        :description-props="{ showStocking: true }"
        :style-color-source-index="index"
        :style-color="styleColor"
        :style-color-set="styleColorSet"
        size="auto"/>
      <RejoinClosetTileCTA
        v-if="styleColor.cta === 'rejoin'"
        :key="`${styleColor.cta}-${index}`"
        :class="styleColorPreviewClass"/>
      <ChatbotClosetTile
        v-if="styleColor.cta === 'chatbot'"
        :key="`${styleColor.cta}-${index}`"
        :class="styleColorPreviewClass"/>
    </template>
    <StyleColorPreviewPlaceholder
      v-for="index in numPlaceholders"
      :key="index"
      :class="styleColorPreviewClass"/>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useWindowScroll, useDebounceFn } from '@vueuse/core'
import StyleColorPreviewPlaceholder from '@shared/components/ADORN/StyleColorPreviewPlaceholder.vue'
import StyleColorPreview from '@shared/components/ADORN/StyleColorPreview.vue'
import TextLink from '@/components/global/sequin/TextLink.vue'
import useStyleColorSet from '@/composables/styleColorSet.js'
import useScreenSize from '@shared/composables/screenSize.js'
import RejoinClosetTileCTA from '@/components/rejoin/RejoinClosetTileCTA.vue'
import { useRejoinStore } from '@/stores/rejoin.js'
import { useConversationalSearchExperiment } from '@shared/composables/experiment.js'
import ChatbotClosetTile from '../chatbot/ChatbotClosetTile.vue'

const props = defineProps({
  styleColorSet: {
    type: Object,
    required: true
  },
  styleColorsLoading: {
    type: Boolean,
    default: false
  },
  selectedFilters: {
    type: Array,
    required: true
  }
})
const emit = defineEmits(['bottom-of-set', 'clear-all-filters'])

const styleColorPreviewClass = 'col-6 col-md-3 col-lg-2 mb-lg'

const { sourceType, styleColors, length } = useStyleColorSet(() => props.styleColorSet)
const hideFilters = computed(() => styleColors.value?.length === 0 && sourceType.value === 'search')
const noStyleColors = computed(() => styleColors.value?.length === 0)

const { screenSizeValue } = useScreenSize()
const rowSize = screenSizeValue({ mobile: 2, tablet: 4, desktop: 6 })
const numPlaceholders = computed(() => {
  if (!props.styleColorsLoading) return 0
  const remainder = (styleColorsWithCTA.value?.length ?? 0) % rowSize.value
  return remainder === 0 ? rowSize.value * 2 : rowSize.value - remainder + rowSize.value * 2 // Show at least 2 full rows
})

const { formerMember } = storeToRefs(useRejoinStore())
const { showVariantChatbot: showChatbot } = useConversationalSearchExperiment()

const styleColorsWithCTA = computed(() => {
  if (!formerMember.value && !showChatbot.value) {
    return styleColors.value
  }
  const result = []
  const totalItems = styleColors.value?.length ?? 0

  // determine where the first CTA (rejoin) should be inserted:
  let insertPosition = totalItems > rowSize.value + 1
    ? rowSize.value + 1 // second row, second position
    : Math.floor(totalItems / 2) // if not enough items, insert in middle of first row

  const ctaFrequency = (rowSize.value * 2) + 1
  styleColors.value?.forEach((styleColor, index) => {
    result.push(styleColor)
    const current = index + 1
    if (formerMember.value && current % insertPosition === 0) {
      result.push({ cta: 'rejoin' })
      insertPosition += ctaFrequency
    }

    // insert "chatbot" CTA only in the second row, second position
    if (showChatbot.value && current === rowSize.value + 1) {
      result.push({ cta: 'chatbot' })
    }
  })
  return result
})


const loadMoreStyleColors = () => {
  emit('bottom-of-set')
}

const isNearBottom = () => {
  const scrollPosition = window.scrollY + window.innerHeight
  const documentHeight = document.documentElement.scrollHeight
  return documentHeight - scrollPosition <= 100
}
const canLoadMore = computed(() => styleColors.value?.length < length.value)

const checkAndLoadMore = () => {
  if (isNearBottom() && !props.styleColorsLoading && canLoadMore.value) {
    loadMoreStyleColors()
  }
}

const debouncedCheckAndLoadMore = useDebounceFn(checkAndLoadMore, 300)

const { y: scrollY } = useWindowScroll()
watch(scrollY, debouncedCheckAndLoadMore)

defineOptions({
  compatConfig: {
    WATCH_ARRAY: false
  }
})
</script>
